<template lang="pug">
.separate-file-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :filter-included-fields="filterFields"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // ヘッダー：利用者グループ
    template(#head(group)="data")
      .d-flex.flex-column.justify-content-center
        span 利用者グループ
        span 部屋名・利用者名
    
    // セル：利用者グループ
    template(#cell(group)="row")
      .d-flex.flex-column
        span {{ row.item.groupName }}
        span {{ row.item.room }}・{{ row.item.residentName }}
    // セル：宛先
    template(#cell(targets)="row")
      .d-flex.flex-column
        span(v-for="family in row.item.targetFamilies") {{ family.label }}
    // セル：登録ファイル
    template(#cell(files)="row")
      .d-flex.flex-column
        ul.list-group.mb-2(v-for="(file, index) in getFiles(row.item)")
          li.list-group-item.file-name(:id="index + file.sortKey") {{ file.name }}
          b-popover(:target="index + file.sortKey" triggers="hover") {{ file.name }}
    // セル：操作
    template(#cell(controll)="row")
      button.btn(@click="onPreview(row.item)") プレビュー

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  SendMessageFilePreviewModal(ref="previewModal")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import S3UrlService from "@/commons/services/S3Url";

import PageLoading from "@/pc/basics/PageLoading.vue";
import DraggableFiles from "@/pc/components/DraggableFiles.vue";
import { SendTarget } from "@/pc/components/forms/FormSendTarget.vue";

import SendMessageFilePreviewModal from "@/pc/components/modals/SendMessageFilePreviewModal.vue";

/**
 * ### 文書送付、「利用者毎に異なる文書」のファイル選択テーブル
 */
@Component({
  components: {
    PageLoading,
    DraggableFiles,
    SendMessageFilePreviewModal
  }
})
export default class SeparateFileTable extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<SeparateFileTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<SeparateFileTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: any;
  /** フィルター対象カラム */
  @Prop({ required: true })
  filterFields!: string[];

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  get _value(): Array<SeparateFileTableItem> {
    return this.value;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: SeparateFileTableItem): boolean {
    if (row.groupId == this.filter) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 添付ファイル名リストを返す
   */
  public getFiles(item: SeparateFileTableItem): Array<{ name: string; sortKey: string }> {
    let files: Array<{ name: string; sortKey: string }> = [];
    for (const file of item.files) {
      files.push({
        name: file.name,
        sortKey: item.residentSortKey
      });
    }

    return files;
  }

  /**
   * ### 添付ファイルプレビューボタン押下イベント処理
   *
   * - ダイアログで添付ファイルをプレビュー表示する
   */
  public async onPreview(item: SeparateFileTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const modal: SendMessageFilePreviewModal | Vue | Element | Vue[] | Element[] = this.$refs.previewModal;
    if (modal instanceof SendMessageFilePreviewModal) {
      const previewSeparateFiles: File[] = [];
      for (const file of item.files) {
        const _file: any = file;
        // 一時保存されていたファイルの場合、初期処理でFileオブジェクトではなく、nameとurlのObject型のデータを格納している
        // urlがある場合はS3からファイルをダウンロードし、Fileオブジェクトに変換する
        if (_file.url != null) {
          const updatedFile: File = await this.getS3File(_file.url, _file.name);
          previewSeparateFiles.push(updatedFile);
        } else {
          previewSeparateFiles.push(file);
        }
      }
      // S3からファイル取得したものも含めて、filesを更新する
      item.files = previewSeparateFiles;

      modal.show("", false, item.files);
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  public async getS3File(url: string, fileName: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url, fileName);
    return response;
  }
}

/** テーブルの列名の情報 */
export interface SeparateFileTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface SeparateFileTableItem {
  residentSortKey: string;
  groupId: number;
  groupName: string;
  residentId: number;
  residentName: string;
  room: string;
  insuredNumberKey: string;

  targetFamilies: Array<SendTarget>;
  files: Array<File>;
}
</script>

<style lang="scss" scoped>
.separate-file-table-component {
  .file-name {
    padding: 6px 12px;
  }
}
</style>
